export class TutorialScene extends Phaser.Scene {
    // Store references to UI elements that need repositioning
    private background!: Phaser.GameObjects.Image;
    private overlay!: Phaser.GameObjects.Rectangle;
    private title!: Phaser.GameObjects.Text;
    private backButton!: Phaser.GameObjects.Container;
    private ruleSections: Array<{
      y: number;
      height: number;
      elements: (Phaser.GameObjects.Rectangle | Phaser.GameObjects.Text)[]
    }> = [];

    constructor() {
      super({ key: 'TutorialScene' });
    }
  
    preload() {
      
      // Always try to load the background image to ensure it's available
      this.load.image('menuBackground', 'assets/images/menu_background.png');
      
      // Add error handling for image loading
      this.load.on('loaderror', (fileObj: Phaser.Loader.File) => {
        console.error('Error loading asset:', fileObj.key);
      });
      
    }

    create() {
      const { width, height } = this.cameras.main;
      
      // Start with a solid background color in case image loading fails
      this.cameras.main.setBackgroundColor('#111111');
      
      // Add background (similar to MenuScene)
      try {
        if (this.textures.exists('menuBackground')) {
          this.background = this.add.image(width / 2, height / 2, 'menuBackground');
          this.updateBackgroundScale();
        } else {
          console.warn('TutorialScene: menuBackground texture does not exist');
        }
      } catch (error) {
        console.error('TutorialScene: Error adding background image', error);
      }
      
      // Add semi-transparent overlay for better text readability
      this.overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7);
      
      // Title with bright color to be visible on any background - move up
      this.title = this.add.text(width / 2, height * 0.08, 'How To Play Metro Tag', {
        fontSize: '48px',
        color: '#ffffff',
        fontFamily: 'Gill Sans, P22 Underground, Arial',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);

      // Create back button - use scene sleep/wake instead of start to preserve music
      this.backButton = this.createStationButton('Back to Main', height - 60, () => {
        // Instead of starting a new MenuScene instance, we'll wake the existing one
        // and sleep this tutorial scene
        this.scene.sleep('TutorialScene');
        this.scene.wake('MenuScene');
      });
      
      // Create underground-style rules display
      this.createUndergroundRules();
      
      // Add resize event handler
      this.scale.on('resize', this.handleResize, this);
    }
    
    private handleResize() {
      // Check if scene is active and cameras system is available
      if (!this.scene.isActive('TutorialScene') || !this.cameras || !this.cameras.main) {
        return;
      }
      
      const { width, height } = this.cameras.main;
      
      // Update background scale and position
      if (this.background) {
        this.background.setPosition(width / 2, height / 2);
        this.updateBackgroundScale();
      }
      
      // Update overlay
      if (this.overlay) {
        this.overlay.setPosition(width / 2, height / 2);
        this.overlay.setSize(width, height);
      }
      
      // Update title position - match the new Y position
      if (this.title) {
        this.title.setPosition(width / 2, height * 0.08);
      }
      
      // Update back button position
      if (this.backButton) {
        this.backButton.setPosition(width / 2, height - 60);
      }
      
      // Only attempt to recreate rule sections if scene is fully active
      if (this.scene.isActive('TutorialScene')) {
        // Recreate rule sections with new dimensions
        this.clearRuleSections();
        this.createUndergroundRules();
      }
    }
    
    private updateBackgroundScale() {
      if (!this.background) return;
      
      const { width, height } = this.cameras.main;
      
      // Calculate scale to cover viewport while maintaining aspect ratio
      const scaleX = width / this.background.width;
      const scaleY = height / this.background.height;
      const scale = Math.max(scaleX, scaleY);
      this.background.setScale(scale);
    }
    
    private clearRuleSections() {
      // Destroy all rule section elements to prevent duplicates
      this.ruleSections.forEach(section => {
        section.elements.forEach(element => {
          element.destroy();
        });
      });
      this.ruleSections = [];
    }
    
    private createStationButton(text: string, y: number, callback: () => void) {
      const { width } = this.cameras.main;
      
      // Create station sign container
      const container = this.add.container(width / 2, y);

      // Add white background rectangle
      const bg = this.add.rectangle(0, 0, 300, 60, 0xFFFFFF);
      container.add(bg);

      // Add red bar on the left
      const leftBar = this.add.rectangle(-130, 0, 40, 60, 0xDE2110);
      container.add(leftBar);

      // Add station name text
      const stationText = this.add.text(0, 0, text, {
        fontFamily: 'Gill Sans, P22 Underground, Arial',
        fontSize: '28px',
        color: '#000000',
        align: 'center'
      }).setOrigin(0.5);
      container.add(stationText);

      // Make container interactive
      container.setSize(300, 60);
      container.setInteractive()
        .on('pointerover', () => {
          bg.setFillStyle(0xEEEEEE);
          this.tweens.add({
            targets: container,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 100
          });
        })
        .on('pointerout', () => {
          bg.setFillStyle(0xFFFFFF);
          this.tweens.add({
            targets: container,
            scaleX: 1,
            scaleY: 1,
            duration: 100
          });
        })
        .on('pointerdown', callback);

      return container;
    }
    
    private createUndergroundRules() {
      const { width, height } = this.cameras.main;
      
      // Calculate vertical spacing based on available space - move everything up by 10%
      const availableHeight = height * 0.8; // Use 80% of screen height for rules
      const startY = height * 0.13; // Start at 13% of screen height (was 20%)
      const padding = height * 0.03; // 3% of screen height for padding
      
      // Define compact rule sets
      const section1 = this.createRuleSection('CONTROLS', startY, [
        '• SPACE: Board/exit trains',
        '• E: Draw challenge cards'
      ]);
      this.ruleSections.push(section1);
      
      // Create a horizontal layout for the bottom two sections when screen height is limited
      if (height < 700) {
        // Calculate positions for a 2-column layout
        const columnWidth = width * 0.4;
        const section2 = this.createRuleSection('RUNNER TIPS', startY + section1.height + padding, [
          '• Use head start period',
          '• Trains help evade capture',
          '• Cards give advantages',
          '• Survive as long as possible'
        ], width / 2 - columnWidth / 2);
        this.ruleSections.push(section2);
        
        const section3 = this.createRuleSection('CHASER TIPS', startY + section1.height + padding, [
          '• Can see Runner location',
          '• Coordinate with team',
          '• Intercept at stations',
          '• Prevent escapes'
        ], width / 2 + columnWidth / 2);
        this.ruleSections.push(section3);
      } else {
        // Use vertical layout for taller screens
        const section2 = this.createRuleSection('RUNNER GUIDELINES', startY + section1.height + padding, [
          '• Start with head start period',
          '• Cannot see Chasers on map',
          '• Use trains to evade capture',
          '• Draw cards for advantages',
          '• Longest survival time wins'
        ]);
        this.ruleSections.push(section2);
        
        const section3 = this.createRuleSection('CHASER INFORMATION', startY + section2.height + section1.height + padding * 1.5, [
          '• Can see Runner\'s location',
          '• Work together to coordinate',
          '• Intercept at stations',
          '• Prevent Runner from escaping'
        ]);
        this.ruleSections.push(section3);
      }
    }
    
    private createRuleSection(title: string, y: number, rules: string[], x?: number): { 
      y: number; 
      height: number; 
      elements: (Phaser.GameObjects.Rectangle | Phaser.GameObjects.Text)[] 
    } {
      const { width } = this.cameras.main;
      const centerX = x !== undefined ? x : width / 2;
      const textWidth = width * 0.35; // Reduce width for better fit
      const rulePadding = 12; // Slightly reduced padding for tighter layout
      
      // Create line indicator in TfL red
      const line = this.add.rectangle(centerX, y - 12, textWidth, 6, 0xDE2110);
      
      // Add section title in Underground style with proper padding
      const titleText = this.add.text(centerX, y, title, {
        fontFamily: 'Gill Sans, P22 Underground, Arial',
        fontSize: '28px', // Smaller font
        color: '#FFFFFF',
        backgroundColor: '#0009AB',
        padding: { x: 16, y: 6 } // Slightly smaller padding
      }).setOrigin(0.5);
      
      // Add rules in Underground signage style with better spacing
      let currentY = y + 40; // Less space after title
      const ruleTexts: Phaser.GameObjects.Text[] = [];
      
      rules.forEach(rule => {
        const ruleText = this.add.text(centerX, currentY, rule, {
          fontFamily: 'Gill Sans, P22 Underground, Arial',
          fontSize: '20px', // Smaller font for rules
          color: '#FFFFFF',
          align: 'center', // Center align for better reading
          padding: { x: 8, y: 3 } // Smaller padding
        }).setOrigin(0.5, 0);
        
        ruleTexts.push(ruleText);
        currentY += ruleText.height + rulePadding;
      });
      
      // Calculate and return the overall section dimensions for easier positioning
      const sectionHeight = currentY - y;
      return {
        y: y,
        height: sectionHeight,
        elements: [line, titleText, ...ruleTexts]
      };
    }

    // Proper lifecycle management for Phaser Scene
    init() {
      console.log('TutorialScene: init');
    }
    
    // Called when scene is about to be put to sleep
    sleep() {
      console.log('TutorialScene: sleep');
    }
    
    // Called when scene wakes up
    wake() {
      console.log('TutorialScene: wake');
      
      // Make sure positions and layout are updated on wake
      const { width, height } = this.cameras.main;
      if (this.title) {
        this.title.setPosition(width / 2, height * 0.08);
      }
      
      if (this.backButton) {
        this.backButton.setPosition(width / 2, height - 60);
      }
    }
    
    // Called when scene is about to be removed
    shutdown() {
      console.log('TutorialScene: shutdown');
      
      // Remove the resize listener to prevent memory leaks
      if (this.scale) {
        this.scale.off('resize', this.handleResize, this);
      }
      
      // Clean up any lingering elements
      this.clearRuleSections();
    }
  } 